import React, { useEffect, useState } from "react";
import { Logo } from "./Logo";
import { Menu, X, ChevronDown } from "lucide-react";

const NAV_ITEMS = [
  {
    label: "About",
    href: "#about",
    children: [
      { label: "About Elena", href: "#about" },
      { label: "Accreditations", href: "#accreditations" },
      { label: "Books", href: "#books" },
    ],
  },
  { label: "Services", href: "#services" },
  { label: "Approach", href: "#approach" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Resources", href: "#resources" },
  { label: "Contact", href: "#contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null); // label of currently expanded mobile sub-menu

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#F9F8F6]/85 border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-24 md:h-28">
        <Logo />

        <nav className="hidden md:flex items-center gap-10">
          {NAV_ITEMS.map((item) =>
            item.children ? (
              <div key={item.label} className="relative group">
                <a
                  href={item.href}
                  data-testid={`nav-link-${item.label.toLowerCase()}`}
                  className="inline-flex items-center gap-1 font-sans-body text-[13px] tracking-widest-2 uppercase text-[#0D0D0D] hover:text-[#3E7373]"
                >
                  {item.label}
                  <ChevronDown size={12} strokeWidth={1.5} className="mt-0.5 transition-transform duration-300 group-hover:rotate-180" />
                </a>
                {/* dropdown */}
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 z-50"
                  data-testid={`nav-dropdown-${item.label.toLowerCase()}`}
                >
                  <div className="min-w-[240px] bg-[#F9F8F6] border border-black/10 shadow-[0_24px_48px_-24px_rgba(0,0,0,0.18)] py-3">
                    {item.children.map((child) => (
                      <a
                        key={child.href}
                        href={child.href}
                        data-testid={`nav-sublink-${child.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="block px-6 py-3 font-sans-body text-[12px] tracking-widest-2 uppercase text-[#0D0D0D] hover:bg-white hover:text-[#3E7373]"
                      >
                        {child.label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className="font-sans-body text-[13px] tracking-widest-2 uppercase text-[#0D0D0D] hover:text-[#3E7373]"
              >
                {item.label}
              </a>
            )
          )}
          <a
            href="#contact"
            data-testid="nav-cta-work-with-me"
            className="font-sans-body text-[13px] tracking-widest-2 uppercase bg-[#0D0D0D] text-white px-6 py-3 hover:bg-[#3E7373]"
          >
            Work with me
          </a>
        </nav>

        <button
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          className="md:hidden inline-flex items-center justify-center w-10 h-10 text-[#0D0D0D]"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={`md:hidden overflow-hidden transition-[max-height] duration-500 ${
          open ? "max-h-[640px] border-b border-black/5 bg-[#F9F8F6]" : "max-h-0"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-4">
          {NAV_ITEMS.map((item) =>
            item.children ? (
              <div key={item.label} className="flex flex-col">
                <button
                  type="button"
                  onClick={() => setOpenSub((cur) => (cur === item.label ? null : item.label))}
                  data-testid={`mobile-nav-toggle-${item.label.toLowerCase()}`}
                  className="flex items-center justify-between font-sans-body text-sm tracking-widest-2 uppercase text-[#0D0D0D]"
                >
                  {item.label}
                  <ChevronDown
                    size={14}
                    strokeWidth={1.5}
                    className={`transition-transform duration-300 ${openSub === item.label ? "rotate-180" : ""}`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-[max-height] duration-300 ${
                    openSub === item.label ? "max-h-40" : "max-h-0"
                  }`}
                >
                  <div className="flex flex-col gap-3 pl-4 pt-4 border-l border-black/10 ml-1 mt-2">
                    {item.children.map((child) => (
                      <a
                        key={child.href}
                        href={child.href}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-sublink-${child.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="font-sans-body text-xs tracking-widest-2 uppercase text-[#4A4A4A] hover:text-[#3E7373]"
                      >
                        {child.label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                className="font-sans-body text-sm tracking-widest-2 uppercase text-[#0D0D0D]"
              >
                {item.label}
              </a>
            )
          )}
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            data-testid="mobile-nav-cta"
            className="font-sans-body text-sm tracking-widest-2 uppercase bg-[#0D0D0D] text-white px-6 py-3 inline-block text-center mt-2"
          >
            Work with me
          </a>
        </div>
      </div>
    </header>
  );
};

export default Nav;
